import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  canAcceptProposalNow,
  type ClubDecisionDto,
  ON_BEHALF_REASONS,
  type OnBehalfReason,
  type TripDto,
} from "@tc/shared";
import {
  Button,
  CostTotal,
  DepositAuthorize,
  Field,
  Icon,
  Modal,
  OptionList,
  OptionRow,
  Segmented,
  Select,
  Textarea,
} from "@/components/ui";
import { useFormatters } from "@/lib/i18n/format";
import { ApiError } from "@/features/trips/api";
import { proposalApi } from "./api";
import { nightsOf } from "./ProposalTab";

type Decision = "accept" | "reject";

type Load = { kind: "loading" } | { kind: "error" } | { kind: "ready"; data: ClubDecisionDto };

const BLOCK_KEY: Record<NonNullable<ClubDecisionDto["acceptBlock"]>, string> = {
  noLiveProposal: "proposal.onBehalf.blockNoLive",
  notAwaitingClub: "proposal.onBehalf.blockNotAwaiting",
  amendedPending: "proposal.onBehalf.blockAmended",
};

/**
 * Back office records the club's decision for it (TRAV-74) — the club answered by phone/email but
 * never responded in the Travel Desk. **Accept** takes the club's own accept payload (line-item
 * selection + deposit authorisation) and works on a lapsed proposal too; **Reject** declines an
 * unanswered amendment and restores the previously accepted version (only offered when one exists).
 * Both require a predefined reason (+ a note for "Other"), recorded on the audit trail; the dialog
 * says up front whether the club will be notified (not once the trip has already taken place).
 */
export function ClubDecisionModal({
  trip,
  onClose,
  onDone,
}: {
  trip: TripDto;
  onClose: () => void;
  /** The decision was recorded — the trip is now Accepted; the page reloads. */
  onDone: () => void;
}) {
  const { t } = useTranslation();
  const fmt = useFormatters();
  const [state, setState] = useState<Load>({ kind: "loading" });
  const [decision, setDecision] = useState<Decision>("accept");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [depositAuthorized, setDepositAuthorized] = useState(false);
  const [reason, setReason] = useState<OnBehalfReason>("phone");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    proposalApi
      .clubDecision(trip.id)
      .then((data) => {
        if (cancelled) return;
        setState({ kind: "ready", data });
        // Pre-select every option, as the club's own review does; untick what the club declined.
        setSelectedIds(new Set(data.live?.lineItems.map((l) => l.id) ?? []));
        // Nothing to accept but something to restore → open straight on Reject.
        if (data.acceptBlock && data.restorable) setDecision("reject");
      })
      .catch(() => !cancelled && setState({ kind: "error" }));
    return () => {
      cancelled = true;
    };
  }, [trip.id]);

  const requiresDeposit = nightsOf(trip) > 0;
  const noteMissing = reason === "other" && note.trim().length === 0;

  async function submit(data: ClubDecisionDto) {
    setError(null);
    setBusy(true);
    const why = { reason, note: note.trim() || undefined };
    try {
      if (decision === "accept") {
        await proposalApi.acceptOnBehalf(trip.id, { selectedLineItemIds: [...selectedIds], depositAuthorized, ...why });
      } else if (data.restorable) {
        await proposalApi.rejectOnBehalf(trip.id, why);
      }
      onDone();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("proposal.onBehalf.error"));
      setBusy(false);
    }
  }

  const body = (() => {
    if (state.kind === "loading") return <div className="empty">{t("common.loading")}</div>;
    if (state.kind === "error") return <Alert text={t("builder.loadError")} />;
    const { data } = state;
    const live = data.live;

    const acceptPanel =
      data.acceptBlock || !live ? (
        <Alert text={t(BLOCK_KEY[data.acceptBlock ?? "noLiveProposal"])} />
      ) : (
        <>
          <p className="muted" style={{ margin: 0, fontSize: 13 }}>
            {t("proposal.onBehalf.acceptDesc", { version: live.version })}
          </p>
          {live.status === "Expired" && (
            <div className="info-banner" role="status">
              <Icon name="clock" size={16} />
              <span>{t("proposal.onBehalf.expiredHint")}</span>
            </div>
          )}
          <OptionList>
            {live.lineItems.map((line) => (
              <OptionRow
                key={line.id}
                selectable
                checked={selectedIds.has(line.id)}
                onToggle={() =>
                  setSelectedIds((prev) => {
                    const next = new Set(prev);
                    if (next.has(line.id)) next.delete(line.id);
                    else next.add(line.id);
                    return next;
                  })
                }
                description={line.description}
                qtyLabel={`${line.qty} × ${fmt.currency(line.unitPrice, live.currency)}`}
                amountLabel={fmt.currency(line.amount, live.currency)}
              />
            ))}
          </OptionList>
          <CostTotal
            label={t("proposal.selectedTotal")}
            amountLabel={fmt.currency(
              live.lineItems.filter((l) => selectedIds.has(l.id)).reduce((sum, l) => sum + l.amount, 0),
              live.currency,
            )}
          />
          {requiresDeposit && (
            <DepositAuthorize
              checked={depositAuthorized}
              onChange={setDepositAuthorized}
              label={t("proposal.onBehalf.depositAuth")}
            />
          )}
        </>
      );

    const rejectPanel =
      data.restorable && live ? (
        <p className="muted" style={{ margin: 0, fontSize: 13 }}>
          {t("proposal.onBehalf.rejectDesc", {
            version: live.version,
            restoredVersion: data.restorable.version,
            date: fmt.date(data.restorable.acceptedAt),
          })}
        </p>
      ) : (
        <Alert text={t("proposal.onBehalf.rejectUnavailable")} />
      );

    return (
      <div className="stack" style={{ gap: 14 }}>
        <p className="muted" style={{ margin: 0 }}>{t("proposal.onBehalf.intro")}</p>
        <Segmented<Decision>
          value={decision}
          onChange={(d) => {
            setError(null);
            setDecision(d);
          }}
          options={[
            { value: "accept", label: t("proposal.onBehalf.decisionAccept"), icon: "check" },
            { value: "reject", label: t("proposal.onBehalf.decisionReject"), icon: "x" },
          ]}
        />
        {decision === "accept" ? acceptPanel : rejectPanel}
        <Field label={t("proposal.onBehalf.reasonLabel")} required>
          <Select
            value={reason}
            onChange={(v) => setReason(v as OnBehalfReason)}
            options={ON_BEHALF_REASONS.map((r) => ({ value: r, label: t(`proposal.onBehalf.reason.${r}`) }))}
          />
        </Field>
        <Field
          label={reason === "other" ? t("proposal.onBehalf.noteLabel") : t("proposal.onBehalf.noteOptional")}
          required={reason === "other"}
          hint={noteMissing ? t("proposal.onBehalf.noteRequired") : undefined}
        >
          <Textarea value={note} onChange={setNote} rows={2} placeholder={t("proposal.onBehalf.notePlaceholder")} />
        </Field>
        <p className="sub" style={{ margin: 0, fontSize: 12 }}>
          {data.notifyClub ? t("proposal.onBehalf.notifyYes") : t("proposal.onBehalf.notifyNo")}
        </p>
        {error && <Alert text={error} />}
      </div>
    );
  })();

  const ready = state.kind === "ready" ? state.data : null;
  const canSubmit =
    ready != null &&
    !busy &&
    !noteMissing &&
    (decision === "accept"
      ? ready.acceptBlock == null &&
        canAcceptProposalNow({ selectedCount: selectedIds.size, requiresDeposit, depositAuthorized })
      : ready.restorable != null && ready.live != null);

  return (
    <Modal open onClose={onClose} title={t("proposal.onBehalf.title")} width={600}>
      <div className="modal-body">{body}</div>
      <div className="modal-foot">
        <Button type="button" variant="ghost" onClick={onClose} disabled={busy}>
          {t("actions.cancel")}
        </Button>
        <Button
          type="button"
          variant={decision === "accept" ? "primary" : "danger"}
          icon={decision === "accept" ? "check" : "x"}
          disabled={!canSubmit}
          onClick={() => ready && submit(ready)}
        >
          {busy
            ? t("common.loading")
            : decision === "accept"
              ? t("proposal.onBehalf.confirmAccept")
              : t("proposal.onBehalf.confirmReject", { version: ready?.restorable?.version ?? "" })}
        </Button>
      </div>
    </Modal>
  );
}

function Alert({ text }: { text: string }) {
  return (
    <div className="auth-alert" role="alert">
      <Icon name="alert" size={16} className="aa-ic" />
      <div className="aa-body">{text}</div>
    </div>
  );
}
